import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveProfile } from '../../services/shopState';
import { createRetailer, getAreas } from '../../services/apiService';
import './ShopSignupPage.css';

// Fallback list, only used when GET /areas is unreachable. The live dropdown
// is populated from the backend so the areaId we register with is always real.
const AREAS = [
  { name: 'Amsterdam Zuid AS',                code: 'AZA' },
  { name: 'Winkelcentrum Amsterdamse Poort',  code: 'WAP' },
  { name: 'Winkelcentrum Gelderlandplein',    code: 'WGP' },
  { name: 'Alexandrium Rotterdam',            code: 'ALR' },
  { name: 'Citymall Almere',                  code: 'CAL' },
  { name: 'Shopping Center Hoge Schie',       code: 'HSR' },
  { name: 'Stadshart Lelystad',               code: 'SHL' },
];

const DAYS = [
  { key: 'mon', label: 'Maandag'   },
  { key: 'tue', label: 'Dinsdag'   },
  { key: 'wed', label: 'Woensdag'  },
  { key: 'thu', label: 'Donderdag' },
  { key: 'fri', label: 'Vrijdag'   },
  { key: 'sat', label: 'Zaterdag'  },
  { key: 'sun', label: 'Zondag'    },
];

const initialHours = () => ({
  mon: { open: '09:00', close: '18:00', closed: false },
  tue: { open: '09:00', close: '18:00', closed: false },
  wed: { open: '09:00', close: '18:00', closed: false },
  thu: { open: '09:00', close: '18:00', closed: false },
  fri: { open: '09:00', close: '18:00', closed: false },
  sat: { open: '10:00', close: '17:00', closed: false },
  sun: { open: '',      close: '',      closed: true  },
});

const rand4 = () => String(Math.floor(1000 + Math.random() * 9000));

// Area code for the display-only locatiecode. Areas that exist in the backend
// but not in the fallback list fall back to the initials of their name.
function areaCodeFor(areaName) {
  const known = AREAS.find((a) => a.name === areaName);
  if (known) return known.code;
  const initials = (areaName || '')
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 3);
  return initials || 'GEN';
}

export default function ShopSignupPage() {
  const navigate = useNavigate();
  const [shopName, setShopName] = useState('');
  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [areaName, setAreaName] = useState('');
  const [address, setAddress] = useState('');
  const [postcode, setPostcode] = useState('');
  const [city, setCity] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [hours, setHours] = useState(initialHours());
  const [holidays, setHolidays] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [apiAreas, setApiAreas] = useState([]);

  // Registration is done with an areaId, so the options come from the backend.
  useEffect(() => {
    let cancelled = false;
    getAreas()
      .then((rows) => {
        if (cancelled || !Array.isArray(rows)) return;
        setApiAreas([...rows].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      })
      .catch(() => { /* keep the fallback AREAS list */ });
    return () => { cancelled = true; };
  }, []);

  const areaOptions = apiAreas.length
    ? apiAreas.map((a) => ({ name: a.name, code: areaCodeFor(a.name) }))
    : AREAS;

  const locationCode = useMemo(() => {
    if (!areaName) return '';
    return `${areaCodeFor(areaName)}-${rand4()}`;
  }, [areaName]);

  const validate = () => {
    const e = {};
    if (!shopName.trim()) e.shopName = 'Vereist';
    if (!email.trim()) e.email = 'Vereist';
    if (pwd.length < 8) e.pwd = 'Minimaal 8 tekens';
    if (pwd !== pwd2) e.pwd2 = 'Wachtwoorden komen niet overeen';
    if (!areaName) e.areaName = 'Kies een gebied';
    if (!address.trim()) e.address = 'Vereist';
    if (!postcode.trim()) e.postcode = 'Vereist';
    if (!city.trim()) e.city = 'Vereist';

    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (loading) return;
    setSubmitError('');
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    const emailKey = email.trim().toLowerCase();

    // Postcode and city have no backend column, so they are folded into the
    // single `address` field to avoid losing them.
    const fullAddress = [address, [postcode, city].filter(Boolean).join(' ')]
      .filter((part) => part && part.trim())
      .join(', ');

    setLoading(true);
    let retailer;
    try {
      const areaId = apiAreas.find((a) => a.name === areaName)?.id || null;
      const res = await createRetailer({
        name: shopName.trim(),
        email: emailKey,
        password: pwd,
        areaId,
        address: fullAddress,
        description,
        instructions,
        openingHours: hours,
      });
      retailer = res.retailer || {};
    } catch (err) {
      setLoading(false);
      if (err?.status === 409) {
        setErrors({ email: 'Dit e-mailadres is al in gebruik' });
      } else {
        setSubmitError(err?.message || 'Aanmelden mislukt. Probeer het opnieuw.');
      }
      return;
    }

    {
      // Mirror to localStorage for the fields the backend does not store yet
      // (tokens, subscription, locatiecode). The account itself now lives in
      // the backend, so login works from any browser.
      const newShop = {
        id: retailer.id,
        email: emailKey,
        password: pwd,
        shopName,
        area: areaName,
        locationCode,
        address,
        postcode,
        city,
        description,
        instructions,
        openingHours: hours,
        closedDays: holidays,
        status: 'actief',
        subscription: 'tokens',
        tokensAvailable: 3,
        tokensPurchased: 3,
        tokensUsed: 0,
        createdAt: new Date().toISOString(),
        lastLogin: null,
        isDemo: false,
      };

      let shops = [];
      try {
        shops = JSON.parse(localStorage.getItem('walkin_shops') || '[]');
      } catch { shops = []; }
      localStorage.setItem('walkin_shops', JSON.stringify([...shops, newShop]));

      saveProfile({
        shopName, email: emailKey, areaName, locationCode,
        address, postcode, city, description,
        hours, holidays,
      });
      setLoading(false);
      setSuccess(true);
    }
  };

  if (success) {
    return (
      <div className="shop-signup">
        <div className="shop-signup__success">
          <div className="shop-signup__success-icon">✅</div>
          <h2 className="shop-signup__success-title">Account aangemaakt!</h2>
          <p className="shop-signup__success-sub">
            Welkom bij WalkIn, {shopName}.
          </p>

          <div className="shop-signup__email-preview">
            <div className="shop-signup__email-head">
              <div><strong>VAN:</strong> welkom@walkin.nl</div>
              <div><strong>AAN:</strong> {email}</div>
              <div><strong>ONDERWERP:</strong> Bevestig je WalkIn account</div>
            </div>
            <div className="shop-signup__email-body">
              <p>Beste {shopName},</p>
              <p>Welkom bij WalkIn!</p>
              <p>Klik op de link om je account te bevestigen:</p>
              <p>
                <button
                  type="button"
                  className="shop-signup__email-cta"
                  onClick={() => navigate('/shop/login')}
                >
                  Bevestig mijn account →
                </button>
              </p>
              <p><strong>Je gegevens:</strong></p>
              <ul>
                <li>Winkelnaam: {shopName}</li>
                <li>Winkelgebied: {areaName}</li>
                <li>Locatiecode: <strong>{locationCode}</strong></li>
                <li>E-mailadres: {email}</li>
              </ul>
              <p>Na bevestiging kun je direct:</p>
              <ul>
                <li>✅ Inloggen op je dashboard</li>
                <li>✅ Je eerste deal aanmaken</li>
                <li>✅ Tokens kopen</li>
              </ul>
              <p>Heb je vragen? Mail ons op hello@walkin.nl</p>
              <p>Met vriendelijke groet,<br />Team WalkIn</p>
            </div>
          </div>

          <button
            className="shop-signup__cta"
            onClick={() => navigate('/shop/login')}
          >
            Ga naar inloggen →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="shop-signup">
      <div className="shop-signup__content">
        <div className="shop-signup__logo">
          <span className="shop-signup__logo-walk">Walk</span>
          <span className="shop-signup__logo-in">In</span>
        </div>
        <div className="shop-signup__badge">Winkeliers modus 🏪</div>

        <h1 className="shop-signup__title">Aanmelden als winkelier</h1>
        <button
          className="shop-signup__login-link"
          type="button"
          onClick={() => navigate('/shop/login')}
        >
          Al een account? Inloggen →
        </button>

        <form className="shop-signup__form" onSubmit={handleSubmit}>
          <label className="shop-signup__field">
            <span className="shop-signup__label">Winkelnaam</span>
            <input
              className="shop-signup__input"
              value={shopName}
              onChange={(e) => setShopName(e.target.value)}
              required
            />
            {errors.shopName && <em className="shop-signup__err">{errors.shopName}</em>}
          </label>

          <label className="shop-signup__field">
            <span className="shop-signup__label">E-mailadres</span>
            <input
              type="email"
              className="shop-signup__input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {errors.email && <em className="shop-signup__err">{errors.email}</em>}
          </label>

          <div className="shop-signup__row">
            <label className="shop-signup__field shop-signup__field--half">
              <span className="shop-signup__label">Wachtwoord</span>
              <input
                type="password"
                className="shop-signup__input"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                required
              />
              {errors.pwd && <em className="shop-signup__err">{errors.pwd}</em>}
            </label>
            <label className="shop-signup__field shop-signup__field--half">
              <span className="shop-signup__label">Herhaal wachtwoord</span>
              <input
                type="password"
                className="shop-signup__input"
                value={pwd2}
                onChange={(e) => setPwd2(e.target.value)}
                required
              />
              {errors.pwd2 && <em className="shop-signup__err">{errors.pwd2}</em>}
            </label>
          </div>

          <label className="shop-signup__field">
            <span className="shop-signup__label">Winkelgebied</span>
            <select
              className="shop-signup__input"
              value={areaName}
              onChange={(e) => setAreaName(e.target.value)}
              required
            >
              <option value="">Kies een gebied</option>
              {areaOptions.map((a) => (
                <option key={a.name} value={a.name}>{a.name}</option>
              ))}
            </select>
            {errors.areaName && <em className="shop-signup__err">{errors.areaName}</em>}
          </label>

          {locationCode && (
            <div className="shop-signup__field">
              <span className="shop-signup__label">Locatiecode</span>
              <div className="shop-signup__code-pill">{locationCode}</div>
            </div>
          )}

          <label className="shop-signup__field">
            <span className="shop-signup__label">Adres</span>
            <input
              className="shop-signup__input"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              required
            />
            {errors.address && <em className="shop-signup__err">{errors.address}</em>}
          </label>

          <div className="shop-signup__row">
            <label className="shop-signup__field shop-signup__field--third">
              <span className="shop-signup__label">Postcode</span>
              <input
                className="shop-signup__input"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                required
              />
            </label>
            <label className="shop-signup__field shop-signup__field--two-thirds">
              <span className="shop-signup__label">Stad</span>
              <input
                className="shop-signup__input"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                required
              />
            </label>
          </div>

          <label className="shop-signup__field">
            <span className="shop-signup__label">
              Beschrijving winkel
              <span className="shop-signup__counter">{description.length}/200</span>
            </span>
            <textarea
              className="shop-signup__textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 200))}
              rows={3}
              maxLength={200}
            />
          </label>

          <label className="shop-signup__field">
            <span className="shop-signup__label">
              Looproute instructies (optioneel)
            </span>
            <textarea
              className="shop__instructions-input"
              placeholder="bijv: Ingang Noord · Verdieping 1 · Naast de H&M · Volg de borden naar 'Mode'"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value.slice(0, 300))}
              rows={3}
              maxLength={300}
            />
            <div className="shop__char-count">{instructions.length}/300</div>
            <div className="shop__instructions-examples">
              <p>Handig voor winkels in een gebouw of winkelcentrum. Voorbeelden:</p>
              <span onClick={() => setInstructions('Verdieping 1 · Ingang Noord · Naast de H&M')}>
                🏬 Winkelcentrum
              </span>
              <span onClick={() => setInstructions('Begane grond · Naast de lift · Volg borden Foodcourt')}>
                🍽️ Foodcourt
              </span>
              <span onClick={() => setInstructions('Parkeergarage ingang B · Lift naar BG')}>
                🚗 Parkeergarage
              </span>
            </div>
          </label>

          <div className="shop-signup__field">
            <span className="shop-signup__label">Openingstijden</span>
            <div className="shop-signup__hours">
              {DAYS.map((d) => (
                <div key={d.key} className="shop-signup__hour-row">
                  <span className="shop-signup__day">{d.label}</span>
                  <label className="shop-signup__closed">
                    <input
                      type="checkbox"
                      checked={hours[d.key].closed}
                      onChange={(e) =>
                        setHours({
                          ...hours,
                          [d.key]: { ...hours[d.key], closed: e.target.checked },
                        })
                      }
                    />
                    Gesloten
                  </label>
                  <input
                    type="time"
                    className="shop-signup__time"
                    value={hours[d.key].open}
                    disabled={hours[d.key].closed}
                    onChange={(e) =>
                      setHours({ ...hours, [d.key]: { ...hours[d.key], open: e.target.value } })
                    }
                  />
                  <span>–</span>
                  <input
                    type="time"
                    className="shop-signup__time"
                    value={hours[d.key].close}
                    disabled={hours[d.key].closed}
                    onChange={(e) =>
                      setHours({ ...hours, [d.key]: { ...hours[d.key], close: e.target.value } })
                    }
                  />
                </div>
              ))}
            </div>
          </div>

          <label className="shop-signup__field">
            <span className="shop-signup__label">Feestdagen / sluitingsdagen</span>
            <textarea
              className="shop-signup__textarea"
              rows={2}
              value={holidays}
              onChange={(e) => setHolidays(e.target.value)}
              placeholder="bijv. 25-12-2026 Eerste Kerstdag"
            />
          </label>

          {submitError && <div className="shop-signup__submit-error">{submitError}</div>}

          <button type="submit" className="shop-signup__submit" disabled={loading}>
            {loading ? 'Bezig...' : 'Account aanmaken'}
          </button>
        </form>
      </div>
    </div>
  );
}
